<template>
  <div>
    <v-app-bar flat color="transparent">
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'settings' }"
          >Back to All Settings</router-link
        >
      </v-toolbar-title>
      <v-spacer />
      <v-row class="mr-2" style="max-width: 200px">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search company"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>
    </v-app-bar>

    <div class="mx-4" id="company-list">
      <h2>{{ name }}</h2>

      <!-- Empty Message -->
      <empty-state
        class="text-center"
        text="No trade found."
        v-show="!loading && !hasTrades"
      />

      <!-- Trades Table -->
      <v-simple-table v-show="!loading && hasTrades">
        <thead>
          <tr>
            <th>Name</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="trade in trades" :key="trade.id">
            <td>{{ trade.name }}</td>
            <td class="text-center">
              <!-- Edit Trade -->
              <v-btn
                text
                color="primary"
                title="Edit Trade"
                :to="{
                  name: 'settings.trades.edit',
                  params: { id: trade.id },
                }"
              >
                <v-icon>{{ icons.mdiPencil }}</v-icon>
              </v-btn>

              <!-- Delete Trade -->
              <v-btn
                text
                color="primary"
                title="Delete Trade"
                @click="deletingTrade(true, trade.id)"
              >
                <v-icon color="red">{{ icons.mdiTrashCan }}</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Loading Skeletons -->
      <div v-show="loading">
        <v-simple-table>
          <thead>
            <tr>
              <th>Name</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <!-- Pagination -->
      <v-pagination
        v-if="!loading"
        v-model="page"
        :length="paginationLength"
        @input="pageChanged"
      ></v-pagination>
    </div>

    <!-- Add Trade Button -->
    <v-btn
      color="primary"
      id="add-trade-button"
      large
      dark
      rounded
      elevation="16"
      @click="addingTrade(true)"
    >
      <v-icon>{{ icons.mdiPlusCircleOutline }}</v-icon>
      <span class="ml-2">Add Trade</span>
    </v-btn>

    <!-- Add Trade Form -->
    <create-trade-form
      v-model="isAddingTrade"
      @onTradeCreated="onTradeCreated"
    />

    <!-- Delete Trade Form -->
    <delete-trade-form
      v-model="trade"
      :show="isDeletingTrade"
      @show="deletingTrade"
      @onTradeDeleted="onTradeDeleted"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false"
          >OK</v-btn
        >
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import EmptyState from '@/components/shared/EmptyState'
import CreateTradeForm from './components/CreateTradeForm.vue'
import DeleteTradeForm from './components/DeleteTradeForm.vue'
import eventBus from '@/services/eventBus'
import { debounce } from 'lodash'
import {
  mdiChevronLeft,
  mdiPlusCircleOutline,
  mdiPencil,
  mdiAccountPlus,
  mdiTrashCan,
} from '@mdi/js'

export default {
  components: {
    EmptyState,
    CreateTradeForm,
    DeleteTradeForm,
  },

  data() {
    return {
      name: 'Trades',

      icons: {
        arrowLeft: mdiChevronLeft,
        mdiPlusCircleOutline,
        mdiPencil,
        mdiAccountPlus,
        mdiTrashCan,
      },

      trade: {
        id: 0,
        name: '',
      },

      filter: {
        search: '',
      },

      loading: false,

      snackbar: {
        show: false,
        message: null,
        color: '',
      },

      listMeta: {
        current_page: 0,
        last_page: 1,
      },

      perPage: 10,
      page: 1,

      isAddingTrade: false,
      isDeletingTrade: false,
    }
  },

  created() {
    this.fetchTrades(1)
  },

  mounted() {
    eventBus.$on('snackbar', ({ message, color }) => {
      this.showSnackbar(message, color)
    })
  },

  beforeDestroy() {
    eventBus.$off(['snackbar'])
  },

  computed: {
    ...mapGetters('trade', ['trades', 'tradesMeta']),

    hasTrades() {
      return this.trades.length > 0
    },

    paginationLength() {
      return Math.ceil(this.tradesMeta.total / this.tradesMeta.per_page) | 0
    },
  },

  methods: {
    ...mapActions({
      getTrades: 'trade/getTrades',
      deleteTrade: 'trade/deleteTrade',
    }),

    async fetchTrades(page, search = null) {
      this.loading = true

      await this.getTrades({
        page: page,
        limit: this.perPage,
        search: search,
      }).finally(() => (this.loading = false))
    },

    pageChanged(page) {
      this.page = page
      this.fetchTrades(this.page)
    },

    addingTrade(status) {
      this.isAddingTrade = status
    },

    onTradeCreated(trade) {
      this.addingTrade(false)
      this.pageChanged(this.page)
    },

    deletingTrade(status, id) {
      this.isDeletingTrade = status
      this.trade = this.trades.find((x) => x.id === id)
    },

    onTradeDeleted() {
      this.deletingTrade(false)
      this.pageChanged(this.page)
    },

    search: debounce(function() {
      this.fetchTrades(this.page, this.filter.search)
    }, 500),

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },
}
</script>

<style lang="scss">
#add-trade-button {
  position: fixed;
  bottom: 64px;
  right: 64px;
}

.v-dialog__container {
  display: unset;
}
</style>
